import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFaceSmile,
  faBolt,
  faMoon,
  faGauge,
  faCalendarDay,
  faPlus,
  faPen,
  faTrash,
  faCheck,
  faExclamationTriangle,
  faBell,
  faSpinner,
  faFire,
  faChevronDown,
  faChevronUp,
  faNotesMedical,
  faHeart,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import mentalWellnessService, { MoodCheckin as MoodCheckinType } from '../../../services/mentalWellnessService';
import BackButton from '../../../components/BackButton/BackButton';
import './MoodCheckin.css';
import { useProfile } from '../../../context/ProfileContext';

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info';
}

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const formatDate = (iso: string): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const formatDateLong = (iso: string): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const todayISO = (): string => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const MOOD_OPTIONS = [
  { value: 1, label: 'Very Bad', emoji: '\uD83D\uDE22', color: '#ef4444' },
  { value: 2, label: 'Bad', emoji: '\uD83D\uDE15', color: '#f97316' },
  { value: 3, label: 'Okay', emoji: '\uD83D\uDE10', color: '#f59e0b' },
  { value: 4, label: 'Good', emoji: '\uD83D\uDE0A', color: '#10b981' },
  { value: 5, label: 'Great', emoji: '\uD83E\uDD29', color: '#7c3aed' },
];

const LEVEL_OPTIONS = [
  { value: 1, label: 'Very Low' },
  { value: 2, label: 'Low' },
  { value: 3, label: 'Moderate' },
  { value: 4, label: 'High' },
  { value: 5, label: 'Very High' },
];

const ENERGY_COLORS: Record<number, string> = { 1: '#ef4444', 2: '#f97316', 3: '#f59e0b', 4: '#10b981', 5: '#7c3aed' };
const SLEEP_COLORS: Record<number, string> = { 1: '#ef4444', 2: '#f97316', 3: '#f59e0b', 4: '#10b981', 5: '#7c3aed' };
const STRESS_COLORS: Record<number, string> = { 1: '#7c3aed', 2: '#10b981', 3: '#f59e0b', 4: '#f97316', 5: '#ef4444' };

const getMoodEmoji = (mood: number): string => MOOD_OPTIONS.find(m => m.value === mood)?.emoji || '\uD83D\uDE10';
const getMoodColor = (mood: number): string => MOOD_OPTIONS.find(m => m.value === mood)?.color || '#94a3b8';
const getMoodLabel = (mood: number): string => MOOD_OPTIONS.find(m => m.value === mood)?.label || 'Unknown';

const HEATMAP_COLORS: Record<number, string> = {
  1: '#fecaca', 2: '#fed7aa', 3: '#fef08a', 4: '#bbf7d0', 5: '#86efac',
};

/* ================================================================== */
/*  Toast Component                                                    */
/* ================================================================== */

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const iconMap: Record<string, IconDefinition> = {
    success: faCheck,
    error: faExclamationTriangle,
    info: faBell,
  };

  return (
    <div className={`mw-mc-toast mw-mc-toast-${type}`}>
      <span className="mw-mc-toast-icon">
        <FontAwesomeIcon icon={iconMap[type]} />
      </span>
      <span className="mw-mc-toast-message">{message}</span>
      <button className="mw-mc-toast-close" onClick={onClose} aria-label="Close notification">
        {'\u00D7'}
      </button>
    </div>
  );
};

/* ================================================================== */
/*  Skeleton                                                           */
/* ================================================================== */

const SkeletonRows: React.FC = () => (
  <div className="mw-mc-skeleton-list">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="mw-mc-skeleton-row">
        <div className="mw-mc-skeleton-circle" />
        <div className="mw-mc-skeleton-lines">
          <div className="mw-mc-skeleton-line long" />
          <div className="mw-mc-skeleton-line medium" />
        </div>
      </div>
    ))}
  </div>
);

/* ================================================================== */
/*  Initial Form State                                                 */
/* ================================================================== */

const INITIAL_FORM = {
  date: todayISO(),
  mood: 0,
  energy_level: 0,
  sleep_quality: 0,
  stress_level: 0,
  gratitude_note: '',
  notes: '',
};

/* ================================================================== */
/*  Main Component                                                     */
/* ================================================================== */

const MoodCheckin: React.FC = () => {
  const { t } = useTranslation(['common']);
  const { activeProfile } = useProfile();

  // Data state
  const [checkins, setCheckins] = useState<MoodCheckinType[]>([]);
  const [heatmapData, setHeatmapData] = useState<Record<string, number>>({});
  const [streak, setStreak] = useState<{ current_streak: number; longest_streak: number }>({ current_streak: 0, longest_streak: 0 });

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [expandedLogId, setExpandedLogId] = useState<number | null>(null);

  // Toast helper
  const showToast = useCallback((message: string, type: ToastState['type']) => {
    setToast({ message, type });
  }, []);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const now = new Date();
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const startDate = thirtyDaysAgo.toISOString().split('T')[0];
      const endDate = now.toISOString().split('T')[0];

      const [checkinsData, streakData, heatmapRes] = await Promise.all([
        mentalWellnessService.getMoodCheckins(),
        mentalWellnessService.getMoodStreak().catch(() => ({ current_streak: 0, longest_streak: 0 })),
        mentalWellnessService.getCalendarHeatmap(startDate, endDate).catch(() => ({})),
      ]);

      setCheckins(Array.isArray(checkinsData) ? checkinsData : []);
      setStreak(streakData);
      if (heatmapRes && typeof heatmapRes === 'object') setHeatmapData(heatmapRes);

      // Check if today's checkin exists
      const todayEntry = (Array.isArray(checkinsData) ? checkinsData : []).find(
        (c: MoodCheckinType) => c.date === todayISO()
      );
      if (todayEntry) {
        setFormData({
          date: todayEntry.date,
          mood: todayEntry.mood || 0,
          energy_level: todayEntry.energy_level || 0,
          sleep_quality: todayEntry.sleep_quality || 0,
          stress_level: todayEntry.stress_level || 0,
          gratitude_note: todayEntry.gratitude_note || '',
          notes: todayEntry.notes || '',
        });
        setEditingId(todayEntry.id);
      }
    } catch (err) {
      console.error('Failed to fetch mood check-in data:', err);
      showToast('Failed to load mood check-in data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData, activeProfile]);

  // Sorted checkins
  const sortedCheckins = useMemo(() => {
    return [...checkins].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [checkins]);

  // Form handlers
  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM);
    setEditingId(null);
  }, []);

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLoadCheckin = (checkin: MoodCheckinType) => {
    setFormData({
      date: checkin.date,
      mood: checkin.mood || 0,
      energy_level: checkin.energy_level || 0,
      sleep_quality: checkin.sleep_quality || 0,
      stress_level: checkin.stress_level || 0,
      gratitude_note: checkin.gratitude_note || '',
      notes: checkin.notes || '',
    });
    setEditingId(checkin.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async () => {
    if (!formData.date) {
      showToast('Please select a date.', 'error');
      return;
    }
    if (formData.mood === 0) {
      showToast('Please select your mood.', 'error');
      return;
    }

    try {
      setSaving(true);
      const payload: any = {
        date: formData.date,
        mood: formData.mood,
      };
      if (formData.energy_level > 0) payload.energy_level = formData.energy_level;
      if (formData.sleep_quality > 0) payload.sleep_quality = formData.sleep_quality;
      if (formData.stress_level > 0) payload.stress_level = formData.stress_level;
      if (formData.gratitude_note.trim()) payload.gratitude_note = formData.gratitude_note.trim();
      if (formData.notes.trim()) payload.notes = formData.notes.trim();

      if (editingId !== null) {
        await mentalWellnessService.updateMoodCheckin(editingId, payload);
        showToast('Mood check-in updated successfully!', 'success');
      } else {
        await mentalWellnessService.createMoodCheckin(payload);
        showToast('Mood check-in saved successfully!', 'success');
      }

      resetForm();
      await fetchData();
    } catch (err) {
      console.error('Error saving mood check-in:', err);
      showToast('Failed to save mood check-in. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await mentalWellnessService.deleteMoodCheckin(id);
      setCheckins((prev) => prev.filter((c) => c.id !== id));
      if (editingId === id) resetForm();
      showToast('Mood check-in deleted.', 'success');
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error deleting mood check-in:', err);
      showToast('Failed to delete mood check-in.', 'error');
    }
  };

  /* ================================================================ */
  /*  Render: Mood Selector                                            */
  /* ================================================================ */

  const renderMoodSelector = () => (
    <div className="mw-mc-form-group">
      <label className="mw-mc-form-label">
        <FontAwesomeIcon icon={faFaceSmile} style={{ marginRight: 6, opacity: 0.5 }} />
        How are you feeling today?
      </label>
      <div className="mw-mc-mood-grid">
        {MOOD_OPTIONS.map((opt) => {
          const isActive = formData.mood === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              className={`mw-mc-mood-btn ${isActive ? 'active' : ''}`}
              style={isActive ? { background: `${opt.color}15`, borderColor: opt.color } : {}}
              onClick={() => handleFieldChange('mood', opt.value)}
            >
              <span className="mw-mc-mood-emoji">{opt.emoji}</span>
              <span className="mw-mc-mood-label" style={isActive ? { color: opt.color } : {}}>{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Render: Level Selector                                           */
  /* ================================================================ */

  const renderLevelSelector = (
    label: string,
    icon: IconDefinition,
    field: string,
    colorMap: Record<number, string>,
  ) => (
    <div className="mw-mc-form-group">
      <label className="mw-mc-form-label">
        <FontAwesomeIcon icon={icon} style={{ marginRight: 6, opacity: 0.5 }} />
        {label}
      </label>
      <div className="mw-mc-level-grid">
        {LEVEL_OPTIONS.map((opt) => {
          const isActive = (formData as any)[field] === opt.value;
          const color = colorMap[opt.value] || '#94a3b8';
          return (
            <button
              key={opt.value}
              type="button"
              className={`mw-mc-level-btn ${isActive ? 'active' : ''}`}
              style={isActive ? { background: color, borderColor: color, color: '#fff' } : { borderColor: `${color}40` }}
              onClick={() => handleFieldChange(field, opt.value)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Render: Calendar Heatmap                                         */
  /* ================================================================ */

  const renderCalendarHeatmap = () => {
    const days: { date: string; mood: number | null }[] = [];
    const now = new Date();
    for (let i = 29; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const dateStr = d.toISOString().split('T')[0];
      days.push({ date: dateStr, mood: heatmapData[dateStr] || null });
    }

    return (
      <div className="mw-mc-heatmap-card">
        <h4 className="mw-mc-heatmap-title">
          <FontAwesomeIcon icon={faCalendarDay} /> Last 30 Days
        </h4>
        <div className="mw-mc-heatmap-grid">
          {days.map((day) => (
            <div
              key={day.date}
              className={`mw-mc-heatmap-cell ${day.mood ? '' : 'empty'}`}
              style={day.mood ? { background: HEATMAP_COLORS[day.mood] } : {}}
              title={`${day.date}: ${day.mood ? getMoodLabel(day.mood) : 'No entry'}`}
            >
              <span className="mw-mc-heatmap-day">
                {new Date(day.date).getDate()}
              </span>
            </div>
          ))}
        </div>
        <div className="mw-mc-heatmap-legend">
          <span className="mw-mc-heatmap-legend-label">Worse</span>
          {[1, 2, 3, 4, 5].map((v) => (
            <div
              key={v}
              className="mw-mc-heatmap-legend-swatch"
              style={{ background: HEATMAP_COLORS[v] }}
            />
          ))}
          <span className="mw-mc-heatmap-legend-label">Better</span>
        </div>
      </div>
    );
  };

  /* ================================================================ */
  /*  Render: Form                                                     */
  /* ================================================================ */

  const renderForm = () => (
    <div className="mw-mc-form-card">
      <div className="mw-mc-form-card-header">
        <h3>
          <FontAwesomeIcon icon={editingId ? faPen : faPlus} />
          {editingId ? 'Update Check-in' : 'Daily Check-in'}
        </h3>
        <div className="mw-mc-today-badge">
          <FontAwesomeIcon icon={faCalendarDay} />
          {formatDateLong(formData.date)}
        </div>
      </div>

      <div className="mw-mc-form-body">
        {/* Date */}
        <div className="mw-mc-form-group">
          <label className="mw-mc-form-label">
            <FontAwesomeIcon icon={faCalendarDay} style={{ marginRight: 6, opacity: 0.5 }} />
            Date
          </label>
          <input
            type="date"
            className="mw-mc-form-input"
            value={formData.date}
            onChange={(e) => handleFieldChange('date', e.target.value)}
          />
        </div>

        {/* Mood */}
        {renderMoodSelector()}

        {/* Energy Level */}
        {renderLevelSelector('Energy Level', faBolt, 'energy_level', ENERGY_COLORS)}

        {/* Sleep Quality */}
        {renderLevelSelector('Sleep Quality', faMoon, 'sleep_quality', SLEEP_COLORS)}

        {/* Stress Level */}
        {renderLevelSelector('Stress Level', faGauge, 'stress_level', STRESS_COLORS)}

        {/* Gratitude Note */}
        <div className="mw-mc-form-group">
          <label className="mw-mc-form-label">
            <FontAwesomeIcon icon={faHeart} style={{ marginRight: 6, opacity: 0.5 }} />
            Gratitude Note (optional)
          </label>
          <textarea
            className="mw-mc-form-textarea"
            placeholder="What are you grateful for today?"
            rows={2}
            value={formData.gratitude_note}
            onChange={(e) => handleFieldChange('gratitude_note', e.target.value)}
          />
        </div>

        {/* Notes */}
        <div className="mw-mc-form-group">
          <label className="mw-mc-form-label">
            <FontAwesomeIcon icon={faNotesMedical} style={{ marginRight: 6, opacity: 0.5 }} />
            Notes (optional)
          </label>
          <textarea
            className="mw-mc-form-textarea"
            placeholder="Any additional thoughts about your day..."
            rows={2}
            value={formData.notes}
            onChange={(e) => handleFieldChange('notes', e.target.value)}
          />
        </div>

        {/* Actions */}
        <div className="mw-mc-form-actions">
          {editingId && (
            <button
              type="button"
              className="mw-mc-btn mw-mc-btn-cancel"
              onClick={resetForm}
            >
              {t('common:buttons.cancel')}
            </button>
          )}
          <button
            type="button"
            className="mw-mc-btn mw-mc-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
            ) : editingId !== null ? (
              <><FontAwesomeIcon icon={faCheck} /> Update Check-in</>
            ) : (
              <><FontAwesomeIcon icon={faPlus} /> Save Check-in</>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Render: History                                                  */
  /* ================================================================ */

  const renderHistory = () => {
    if (loading) return <SkeletonRows />;

    if (sortedCheckins.length === 0) {
      return (
        <div className="mw-mc-empty-state">
          <div className="mw-mc-empty-icon-wrap">
            <FontAwesomeIcon icon={faFaceSmile} />
          </div>
          <h3>No mood check-ins yet</h3>
          <p>Start tracking your mood to discover patterns and build healthier habits.</p>
        </div>
      );
    }

    return (
      <div className="mw-mc-history-list">
        {sortedCheckins.map((checkin, idx) => {
          const moodColor = getMoodColor(checkin.mood);
          const moodEmoji = getMoodEmoji(checkin.mood);
          const isExpanded = expandedLogId === checkin.id;

          return (
            <div
              key={checkin.id}
              className={`mw-mc-history-item ${isExpanded ? 'expanded' : ''}`}
              style={{ animationDelay: `${idx * 0.04}s` }}
            >
              <div
                className="mw-mc-history-row"
                onClick={() => setExpandedLogId(isExpanded ? null : checkin.id)}
              >
                <div className="mw-mc-history-date">
                  <span className="mw-mc-history-emoji">{moodEmoji}</span>
                  <div>
                    <span className="mw-mc-history-date-text">{formatDate(checkin.date)}</span>
                    {checkin.date === todayISO() && (
                      <span className="mw-mc-today-label">Today</span>
                    )}
                  </div>
                </div>
                <div className="mw-mc-history-badges">
                  <span className="mw-mc-mini-badge" style={{ background: `${moodColor}15`, color: moodColor, borderColor: `${moodColor}30` }}>
                    {getMoodLabel(checkin.mood)}
                  </span>
                  {checkin.energy_level && (
                    <span className="mw-mc-mini-badge" style={{ background: `${ENERGY_COLORS[checkin.energy_level]}15`, color: ENERGY_COLORS[checkin.energy_level], borderColor: `${ENERGY_COLORS[checkin.energy_level]}30` }}>
                      <FontAwesomeIcon icon={faBolt} /> E:{checkin.energy_level}
                    </span>
                  )}
                  {checkin.stress_level && (
                    <span className="mw-mc-mini-badge" style={{ background: `${STRESS_COLORS[checkin.stress_level]}15`, color: STRESS_COLORS[checkin.stress_level], borderColor: `${STRESS_COLORS[checkin.stress_level]}30` }}>
                      <FontAwesomeIcon icon={faGauge} /> S:{checkin.stress_level}
                    </span>
                  )}
                </div>
                <div className="mw-mc-history-expand-icon">
                  <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                </div>
              </div>

              {isExpanded && (
                <div className="mw-mc-history-details">
                  <div className="mw-mc-history-detail-grid">
                    {checkin.sleep_quality && (
                      <div className="mw-mc-history-detail-item">
                        <span className="mw-mc-detail-label">Sleep Quality</span>
                        <span className="mw-mc-detail-value">{LEVEL_OPTIONS.find(l => l.value === checkin.sleep_quality)?.label || checkin.sleep_quality}</span>
                      </div>
                    )}
                    {checkin.energy_level && (
                      <div className="mw-mc-history-detail-item">
                        <span className="mw-mc-detail-label">Energy</span>
                        <span className="mw-mc-detail-value">{LEVEL_OPTIONS.find(l => l.value === checkin.energy_level)?.label || checkin.energy_level}</span>
                      </div>
                    )}
                    {checkin.stress_level && (
                      <div className="mw-mc-history-detail-item">
                        <span className="mw-mc-detail-label">Stress</span>
                        <span className="mw-mc-detail-value">{LEVEL_OPTIONS.find(l => l.value === checkin.stress_level)?.label || checkin.stress_level}</span>
                      </div>
                    )}
                  </div>

                  {checkin.gratitude_note && (
                    <div className="mw-mc-history-notes">
                      <FontAwesomeIcon icon={faHeart} />
                      <span>{checkin.gratitude_note}</span>
                    </div>
                  )}

                  {checkin.notes && (
                    <div className="mw-mc-history-notes">
                      <FontAwesomeIcon icon={faNotesMedical} />
                      <span>{checkin.notes}</span>
                    </div>
                  )}

                  <div className="mw-mc-history-item-actions">
                    <button
                      className="mw-mc-action-btn mw-mc-action-edit"
                      onClick={(e) => { e.stopPropagation(); handleLoadCheckin(checkin); }}
                    >
                      <FontAwesomeIcon icon={faPen} /> Edit
                    </button>
                    <button
                      className="mw-mc-action-btn mw-mc-action-delete"
                      onClick={(e) => { e.stopPropagation(); setConfirmDelete(checkin.id); }}
                    >
                      <FontAwesomeIcon icon={faTrash} /> Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  /* ================================================================ */
  /*  Main Render                                                      */
  /* ================================================================ */

  return (
    <div className="mw-mc-page">
      {/* Toast */}
      {toast && (
        <div className="mw-mc-toast-container">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete !== null && (
        <div className="mw-mc-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="mw-mc-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="mw-mc-confirm-icon">
              <FontAwesomeIcon icon={faTrash} />
            </div>
            <h3>Delete Mood Check-in?</h3>
            <p>Are you sure you want to delete this check-in? This action cannot be undone.</p>
            <div className="mw-mc-confirm-actions">
              <button className="mw-mc-btn mw-mc-btn-cancel" onClick={() => setConfirmDelete(null)}>
                Cancel
              </button>
              <button className="mw-mc-btn mw-mc-btn-danger" onClick={() => handleDelete(confirmDelete)}>
                <FontAwesomeIcon icon={faTrash} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Banner */}
      <section className="mw-mc-hero">
        <div className="mw-mc-hero-circle mw-mc-hero-circle-1" />
        <div className="mw-mc-hero-circle mw-mc-hero-circle-2" />
        <Container>
          <BackButton />
          <div className="mw-mc-hero-content">
            <h1 className="mw-mc-hero-title">Daily Mood Check-in</h1>
            <p className="mw-mc-hero-subtitle">
              Track your mood, energy, sleep quality, and stress levels daily to discover patterns and take charge of your wellbeing.
            </p>
          </div>
        </Container>
      </section>

      {/* Main Content */}
      <Container>
        {/* Streak + Today Strip */}
        <div className="mw-mc-top-strip">
          <div className="mw-mc-today-strip-inner">
            <FontAwesomeIcon icon={faCalendarDay} />
            <span>{formatDateLong(todayISO())}</span>
          </div>
          <div className="mw-mc-streak-badge">
            <FontAwesomeIcon icon={faFire} />
            <span>{streak.current_streak} day streak</span>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="mw-mc-main-grid">
          {/* Left: Form */}
          <div className="mw-mc-main-left">
            {renderForm()}
          </div>

          {/* Right: Heatmap + History */}
          <div className="mw-mc-main-right">
            {renderCalendarHeatmap()}

            <div className="mw-mc-history-card">
              <div className="mw-mc-history-card-header">
                <h3>
                  <FontAwesomeIcon icon={faCalendarDay} />
                  Check-in History
                </h3>
                <span className="mw-mc-history-count">{checkins.length} entries</span>
              </div>
              {renderHistory()}
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default MoodCheckin;
